/*
 * Copyright (c) 2019. Legalbird eine Marke der R&S Internet Jewels GmbH
 */
import React from "react";
import Grid from "@mui/material/Grid";
import _ from "lodash";
import Valuable from "./Valuable/Valuable";
import useForm from "../../../hooks/useForm";
import ValidatorForm from "../../Validator/ValidatorForm";
import ValidatorSelect from "../../Validator/ValidatorSelect";
import { formValue } from "../../../services/formServiceFunctions";
import MenuItem from "@mui/material/MenuItem";
import SubmitButton from "../../Button/SubmitButton";
import ValidatorNumberField from "../../Validator/ValidatorNumberField";
import { Box, SelectChangeEvent } from "@mui/material";
import { processCostSupportStyle } from "../processCostSupportStyle";
import { requiredFieldDefault } from "../../../services/validationRules";
import { ProcessCostSupportFormProps } from "../../../types/ProcessCostSupport";

const CashValuables = ({ pcs, onSubmit, isLoading }: ProcessCostSupportFormProps) => {
  const initialValues = {
    cashAmount: pcs.cashAmount,
    hasValuables: pcs.hasValuables,
    valuables: pcs.valuables,
  };

  const { values, errors, handleChange, handleSubmit, handleBlur, registerValidators, touchedValues } = useForm({
    initialValues,
    onSubmit,
    identifier: pcs,
  });

  const add = () => {
    const empty = {
      name: "",
      ownership: "",
      value: "",
    };

    const valuables = values.valuables || [];
    if (valuables.length >= 3) {
      return;
    }

    valuables.push(empty);

    handleChange({
      target: {
        type: "other",
        name: "valuables",
        value: valuables,
      },
    });
  };

  const handleMore = ({ target }: SelectChangeEvent) => {
    const { name, value } = target;
    if (value === "yes") {
      add();
    }
    if (value === "no") {
      const splitName = _.split(name, "-");
      const lastPart = splitName[splitName.length - 1];
      const key = parseInt(lastPart);
      handleChange({
        target: {
          type: "other",
          name: "valuables",
          value: _.slice(values.valuables, 0, key + 1),
        },
      });
    }
  };

  return (
    <>
      <Box sx={processCostSupportStyle.sectionHeadline}>Bargeld / Wertgegenstände</Box>
      <ValidatorForm onSubmit={handleSubmit}>
        <Grid container sx={processCostSupportStyle.grid}>
          <Grid sx={processCostSupportStyle.question} size={12}>
            Wie viel Bargeld besitzen Sie?
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4
            }}>
            <ValidatorNumberField
              fieldType={"money"}
              numberType={"float"}
              fullWidth={true}
              id="cashAmount"
              name="cashAmount"
              label="Betrag"
              onBlur={handleBlur}
              validators={requiredFieldDefault}
              isHighlighted={true}
              registerValidators={registerValidators}
              error={!!errors["cashAmount"]}
              helperText={errors["cashAmount"] || "Wenn Sie kein Bargeld besitzen, geben Sie einfach '0' an"}
              margin="normal"
              value={values.cashAmount}
              onChange={handleChange}
            />
          </Grid>
          <Grid sx={processCostSupportStyle.question} size={12}>
            Besitzen Sie Wertgegenstände?
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4
            }}>
            <ValidatorSelect
              label={"Wertgegenstände"}
              name={"hasValuables"}
              value={formValue(values, "hasValuables")}
              onChange={handleChange}
              onBlur={handleBlur}
            >
              <MenuItem value={"no"}>Nein</MenuItem>
              <MenuItem value={"yes"}>Ja</MenuItem>
            </ValidatorSelect>
          </Grid>
          {formValue(values, "hasValuables") === "yes" && (
            <>
              {_.map(values.valuables, (valuable, key) => (
                <React.Fragment key={key}>
                  <Valuable path={`valuables[${key}]`} valuable={valuable} handleChange={handleChange} />
                  {parseInt(key) < 2 && (
                    <>
                      <Grid sx={processCostSupportStyle.question} size={12}>
                        Möchten Sie einen weiteren Wertgegenstand angeben?
                      </Grid>
                      <Grid
                        size={{
                          xs: 12,
                          md: 6,
                          lg: 4
                        }}>
                        <ValidatorSelect
                          label={"Weiteren Wertgegenstand"}
                          name={`hasMore-${key}`}
                          value={formValue(values, "valuables").length - 1 > parseInt(key) ? "yes" : "no"}
                          onChange={handleMore}
                          onBlur={handleBlur}
                        >
                          <MenuItem value={"no"}>Nein</MenuItem>
                          <MenuItem value={"yes"}>Ja</MenuItem>
                        </ValidatorSelect>
                      </Grid>
                    </>
                  )}
                </React.Fragment>
              ))}
            </>
          )}
          <Grid size={12}>
            <SubmitButton
              error={_.keys(errors).length > 0}
              isLoading={isLoading}
              disabled={_.keys(touchedValues).length === 0}
              variant={"contained"}
              type={"submit"}
            >
              Änderungen speichern
            </SubmitButton>
          </Grid>
        </Grid>
      </ValidatorForm>
    </>
  );
};

export default CashValuables;
